import * as React from 'react';

import { Notification } from 'farmhub-ui-core';
import { useHistory } from 'react-router-dom';

import { SelectedEntityDomain } from 'api';
import { useAppDispatch, useAppSelector } from 'app';
import { entityTypes, entityTypesNumber } from 'commons';
import { MenuSelection } from 'components';
import {
  BoxColorIcon,
  FarmerColorIcon,
  PeopleLinkColorIcon,
  ShopColorIcon,
  TruckColorIcon,
  UserColorIcon,
  VolunteerColorIcon,
} from 'components/Icon';
import { env } from 'env';
import {
  findOptionsWithAuth,
  findWithAuthHavePagination,
  resetCreateOrderData,
  resetQuickStartStep,
  selectAccount,
  selectAppDevice,
  selectOneAccount,
  updateInitLoad,
  updateIsReloadThenDeleteOption,
  updateIsUpdateOption,
} from 'slice';
import {
  DEVICES,
  formatFullTextSearchValue,
  handleRedirect,
  COLLABORATOR_TEXT,
  ENTITY_STATUS,
  ENTITY_TYPE,
  ACTIVE_STATUS,
} from 'utils';

import MyEntitiesDesktop from './MyEntitiesDesktop';
import MyEntitiesMobile from './MyEntitiesMobile';

interface MyEntitiesProps {
  url?: string;
  directLink?: string;
  isResetData?: boolean;
  setCurrentStep?: (e: any) => void;
  setVisibleModal?: (e: boolean) => void;
  onSelectAccount?: (e: boolean) => void;
}

const MyEntities = ({
  url,
  directLink = '',
  isResetData,
  setCurrentStep = () => 0,
  setVisibleModal = () => 0,
  onSelectAccount,
}: MyEntitiesProps): JSX.Element => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const appDevice = useAppSelector(selectAppDevice);
  const [entityType, setEntityType] = React.useState('all');
  const [searchValue, setSearchValue] = React.useState('');
  const [optionIDs, setOptionIDs] = React.useState<string[]>(['all']);
  const [isFilterChange, setIsFilterChange] = React.useState(false);
  const [accountList, setAccountList] = React.useState<any[]>([]);
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: appDevice.device == DEVICES.DESKTOP ? 10 : 15,
  });
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingOptions, setIsLoadingOptions] = React.useState(false);
  const [totalAccount, setTotalAccount] = React.useState(0);
  const user = useAppSelector(state => state.auth.item);
  const [isSearchMobile, setIsSearchMobile] = React.useState(false);
  const refMobile = React.useRef<HTMLDivElement>(null);
  const reloadAccountsFlag = useAppSelector(state => state.account.reloadFlag);
  const account = useAppSelector(selectAccount);
  const [isFirstLoadMobile, setIsFirstLoadMobile] = React.useState(false);

  React.useEffect(() => {
    if (isResetData) {
      setSearchValue('');
    }
  }, [isResetData]);

  React.useEffect(() => {
    console.log(account.isFirstLoading, account.isFirstSelectedAccount);
    if (
      !account.isFirstLoading &&
      account.isFirstSelectedAccount &&
      account.list.length == 0
    ) {
      if (account.totalAccount == 0) {
        history.push('/account/template');
      }
    }
  }, [account.isFirstLoading, account.isFirstSelectedAccount]);

  const handleCreateAccount = (): void => {
    if (url) {
      history.push(url);
    } else {
      setCurrentStep(1);
    }
  };

  React.useEffect(() => {
    if (account.isReloadThenDeleteOption) {
      const newOptionIDs = optionIDs.filter(
        id =>
          account.option.list.find(option => option?.id == id) || id == 'all'
      );

      if (optionIDs.length != newOptionIDs.length) {
        if (newOptionIDs.length == 0) {
          newOptionIDs.push('all');
        }
        setOptionIDs(newOptionIDs);
      }

      dispatch(updateIsReloadThenDeleteOption(false));
    }
  }, [account.isReloadThenDeleteOption]);

  React.useEffect(() => {
    if (account.isUpdateOption) {
      if (optionIDs.length > 0 && optionIDs[0] != 'all') {
        const newAccountList = accountList.filter(
          item =>
            item.entityOptions.length > 0 &&
            optionIDs.find(id => id == item.entityOptions[0].option.id)
        );

        setAccountList(newAccountList);
      }

      dispatch(updateIsUpdateOption(false));
    }
  }, [account.isUpdateOption]);

  React.useEffect(() => {
    const initAccountList = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result: any = await dispatch(
          findWithAuthHavePagination({
            body: {
              ...(optionIDs[0] != 'all' ? { entityOptionIDs: optionIDs } : {}),
            },
            query: {
              page: pagination.current,
              take: pagination.pageSize,

              ...(entityType != 'all'
                ? { type: entityTypesNumber[entityType] }
                : {}),
              ...(searchValue
                ? { name: formatFullTextSearchValue(searchValue, false) }
                : {}),
            },
          })
        ).unwrap();

        const newData = result?.data?.map((item: any) => {
          return {
            ...item,
            key: item.id,
            img: item.avatar,
          };
        });
        setTotalAccount(result.pagination.total);

        if (appDevice.device == DEVICES.MOBILE && pagination.current > 1) {
          setAccountList([...accountList, ...newData]);
        } else {
          setAccountList(newData);
        }
        if (
          appDevice.device == DEVICES.MOBILE &&
          result.pagination.total == 1 &&
          entityType == 'all' &&
          account.isInitLoad
        ) {
          const item = newData[0];
          handleSelectedAccount({
            adminDomain: item.adminDomain,
            id: item.id,
            isOpenNewTab: item.isOpenNewTab,
            role: item.role,
            status: item.status,
            type: item.type,
            domainStatus: item.domainStatus,
            employeeStatus: item.employeeStatus,
            isSkipCheckAccess: item.isSkipCheckAccess,
          });
          dispatch(updateInitLoad());
        }
      } catch (error: any) {
        if (error.message === 'Invalid access first time domain') {
          return;
        }
        Notification('error', 'Tải dữ liệu thất bại', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAccountList();
  }, [
    dispatch,
    history,
    user?.id,
    pagination.current,
    pagination.pageSize,
    optionIDs,
    isFilterChange,
  ]);

  const handleChangePagination = (page: number, pageSize?: number): void => {
    setPagination(prevData => {
      return {
        current: page,
        pageSize: pageSize || prevData.pageSize,
      };
    });
  };

  const handleChangeSearchValue = (e: string): void => {
    setSearchValue(e);

    if (refMobile.current && DEVICES.MOBILE == appDevice.device) {
      refMobile.current.scroll({
        top: -45,
        behavior: 'smooth',
      });
    }

    if (DEVICES.DESKTOP == appDevice.device) {
      if (pagination.current == 1) {
        setIsFilterChange(!isFilterChange);
      } else {
        setPagination(prevData => {
          return {
            ...prevData,
            current: 1,
          };
        });
      }
    } else {
      setIsSearchMobile(!isSearchMobile);
    }
  };

  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (DEVICES.MOBILE == appDevice.device) {
        if (isFirstLoadMobile) {
          if (pagination.current == 1) {
            setIsFilterChange(!isFilterChange);
          } else {
            setPagination(prevData => {
              return {
                ...prevData,
                current: 1,
              };
            });
          }
        } else {
          setIsFirstLoadMobile(true);
        }
      }
    }, 800);

    return () => clearTimeout(delayDebounceFn);
  }, [isSearchMobile]);

  const handleChangeEntityType = (e): void => {
    setEntityType(e);

    if (pagination.current == 1) {
      setIsFilterChange(!isFilterChange);
    } else {
      setPagination(prevData => {
        return {
          ...prevData,
          current: 1,
        };
      });
    }

    if (refMobile.current && DEVICES.MOBILE == appDevice.device) {
      refMobile.current.scroll({
        top: -45,
        behavior: 'smooth',
      });
    }
  };

  const handleSelectedAccount = async (
    value: SelectedEntityDomain
  ): Promise<void> => {
    const { id, type, status, adminDomain, employeeStatus, isSkipCheckAccess } =
      value;

    // const isDeveloper =
    //   currentDomain == `${env.app.host}:${env.app.port}` ||
    //   currentDomain.includes('loca.lt');

    // if (!isSkipCheckAccess) {
    //   if (employeeStatus == ACTIVE_STATUS.DEACTIVATED) {
    //     Notification('warning', 'Tài khoản nhân viên đang dừng hoạt động');
    //     return;
    //   }

    //   if (adminDomain == null) {
    //     Notification('warning', 'Tên miền ứng dụng chưa tồn tại');
    //     return;
    //   }

    //   if (isDeveloper == false && `https://${currentDomain}` != adminDomain) {
    //     const newUrl = `${adminDomain}/admin/account/list?entityID=${id}`;
    //     window.open(newUrl, '_blank', 'noopener,noreferrer');
    //     return;
    //   }
    // }

    onSelectAccount && onSelectAccount(true);
    setVisibleModal(false);

    if (status === ENTITY_STATUS.REJECT || status === ENTITY_STATUS.PENDING) {
      if (directLink !== '') {
        handleRedirect(`/${entityTypes[type]}/${id}/detail`);
      } else {
        history.push(`/${entityTypes[type]}/${id}/detail`);
      }
      onSelectAccount && onSelectAccount(false);
      return;
    }

    if (directLink !== '' && Number(type) === ENTITY_TYPE.COLLABORATOR) {
      handleRedirect(`/`);
    }

    try {
      await dispatch(selectOneAccount(id)).unwrap;

      dispatch(resetCreateOrderData());
      dispatch(resetQuickStartStep());

      history.push(`/${directLink ? `${directLink}` : ''}`);
      setVisibleModal(false);
    } catch (error: any) {
      Notification('error', 'Tải dữ liệu thất bại', error);
    } finally {
      onSelectAccount && onSelectAccount(false);
    }
  };

  React.useEffect(() => {
    const initAccountList = async () => {
      try {
        setIsLoadingOptions(true);
        await dispatch(findOptionsWithAuth()).unwrap();
      } catch (error: any) {
        Notification('error', 'Tải dữ liệu thất bại', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    if (account.reloadOptionsFlag || account?.option?.list?.length == 0) {
      initAccountList();
    }
  }, [dispatch, reloadAccountsFlag, account.reloadOptionsFlag]);

  const renderEntityName = (type: string) => {
    switch (type) {
      case 'farm':
        return {
          name: 'Nông trại',
          icon: <FarmerColorIcon height={84} width={84} />,
        };
      case 'store':
        return {
          name: 'Cửa hàng',
          icon: <ShopColorIcon height={84} width={84} />,
        };
      case 'collaborator':
        return {
          name: `${COLLABORATOR_TEXT.CAPITALIZE}`,
          icon: <PeopleLinkColorIcon height={84} width={84} />,
        };
      case 'transport':
        return {
          name: 'Đơn vị vận chuyển',
          icon: <TruckColorIcon height={84} width={84} />,
        };
      case 'warehouse':
        return {
          name: 'Kho chứa',
          icon: <BoxColorIcon height={84} width={84} />,
        };
      case 'volunteer':
        return {
          name: 'Thiện nguyện',
          icon: <VolunteerColorIcon height={84} width={84} />,
        };
      default:
        return {
          name: 'Tài khoản',
          icon: <UserColorIcon height={84} width={84} />,
        };
    }
  };

  const handleFilterByTag = (id: string): void => {
    setIsFilterChange(!isFilterChange);

    if (id === 'all') {
      setOptionIDs(['all']);
    } else {
      if (optionIDs.includes(id)) {
        setOptionIDs(prevState => {
          const newList = prevState.filter(
            item => item !== id && item !== 'all'
          );
          if (newList.length === 0) {
            return ['all'];
          }

          return newList;
        });
      } else {
        setOptionIDs(prevState =>
          prevState.concat(id).filter(item => item !== 'all')
        );
      }
    }

    if (appDevice.device == DEVICES.MOBILE) {
      setPagination(prevState => {
        return {
          ...prevState,
          current: 1,
        };
      });
    }
  };

  const handleChangeAccountList = (e: any[]): void => {
    setAccountList(e);
  };

  const renderElements = {
    [DEVICES.DESKTOP]: (
      <MyEntitiesDesktop
        currentList={accountList}
        entityType={entityType}
        handleChangeAccountList={handleChangeAccountList}
        handleChangePagination={handleChangePagination}
        handleFilterByTag={handleFilterByTag}
        handleSelectedAccount={handleSelectedAccount}
        isFilterChange={isFilterChange}
        isLoadingOptions={isLoadingOptions}
        loading={isLoading}
        pagination={pagination}
        renderEntityName={renderEntityName}
        selectedOptionIDs={optionIDs}
        total={totalAccount}
      >
        <MenuSelection
          entityType={entityType}
          handleChangeSearchValue={handleChangeSearchValue}
          searchValue={searchValue}
          setEntityType={handleChangeEntityType}
        />
      </MyEntitiesDesktop>
    ),
    [DEVICES.MOBILE]: (
      <MyEntitiesMobile
        currentList={accountList}
        entityType={entityType}
        handleChangePagination={handleChangePagination}
        handleCreateAccount={handleCreateAccount}
        handleSelectedAccount={handleSelectedAccount}
        list={accountList}
        loading={isLoading}
        pagination={pagination}
        refMobile={refMobile}
        renderEntityName={renderEntityName}
        total={totalAccount}
      >
        <MenuSelection
          entityType={entityType}
          handleChangeSearchValue={handleChangeSearchValue}
          searchValue={searchValue}
          setEntityType={handleChangeEntityType}
        />
      </MyEntitiesMobile>
    ),
    [DEVICES.TABLET]: (
      <MyEntitiesMobile
        currentList={accountList}
        entityType={entityType}
        handleChangePagination={handleChangePagination}
        handleCreateAccount={handleCreateAccount}
        handleSelectedAccount={handleSelectedAccount}
        list={accountList}
        loading={isLoading}
        pagination={pagination}
        refMobile={refMobile}
        renderEntityName={renderEntityName}
        total={totalAccount}
      >
        <MenuSelection
          entityType={entityType}
          handleChangeSearchValue={handleChangeSearchValue}
          searchValue={searchValue}
          setEntityType={handleChangeEntityType}
        />
      </MyEntitiesMobile>
    ),
  };

  return renderElements[appDevice.device];
};

export default MyEntities;
